'use client';

import {
  useContext,
  useMemo,
  useState,
  useCallback,
  createContext,
} from 'react';
import type {
  DropLinksContextType,
  DropLinksProviderProps,
} from './DropLinksProvider.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '../UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import {
  getDropLinks,
  createDropLink as createDropLinkAPI,
  updateDropLink as updateDropLinkAPI,
  deleteDropLink as deleteDropLinkAPI,
} from '@/_api/drop-link';
import { DropLink } from '@/_types/models/dropLink';
import invariant from 'tiny-invariant';
import { EditDropLinkDialog } from './components/EditDropLinkDialog';
import { CreateDropLinkDialog } from './components/CreateDropLinkDialog';
import { DropLinkUploadsDialog } from './components/DropLinkUploadsDialog';

const DropLinksContext = createContext<DropLinksContextType>({
  // Dialog states
  showCreateModal: false,
  showEditModal: false,
  showUploadsFor: null,

  // Dialog handlers
  onCreateDialogClose: () => {},
  onEditDialogClose: () => {},
  onUploadsDialogClose: () => {},

  // Drop link states
  dropLinks: [],
  isLoading: false,
  selectedDropLink: null,
  newDropLinkName: '',
  newDropLinkHideSignIn: false,
  newDropLinkUserIds: new Set(),
  newDropLinkCustomInstruction: '',
  firmUserOptions: [],
  isActive: false,
  unseenDropLinkUploadsCount: 0,

  // State setters
  setActive: () => {},
  setSelectedDropLink: () => {},
  setNewDropLinkName: () => {},
  setNewDropLinkHideSignIn: () => {},
  setNewDropLinkUserIds: () => {},
  setNewDropLinkCustomInstruction: () => {},
  setShowCreateModal: () => {},
  setShowEditModal: () => {},
  setShowUploadsFor: () => {},

  // Actions
  handleCreateDropLink: () => {},
  updateDropLink: () => {},
  deleteDropLink: () => {},
  loadDropLinks: () => {},
  resetCreateDropLinkForm: () => {},
  closeAllDropLinkDialogs: () => {},
});

const DropLinksProvider = ({ children }: DropLinksProviderProps) => {
  const { addToast } = useContext(ToastContext);
  const { firmUsers, preferences } = useContext(FirmSettingsContext);
  const { token, isAdvisor, user } = useContext(UserContext);

  // Dialog states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadsFor, setShowUploadsFor] = useState<DropLink | null>(null);

  // Drop link states
  const [isActive, setActive] = useState<boolean>(false);
  const [dropLinks, setDropLinks] = useState<DropLink[]>([]);
  const [selectedDropLink, setSelectedDropLink] = useState<DropLink | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [newDropLinkName, setNewDropLinkName] = useState('');
  const [newDropLinkHideSignIn, setNewDropLinkHideSignIn] = useState(false);
  const [newDropLinkUserIds, setNewDropLinkUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [newDropLinkCustomInstruction, setNewDropLinkCustomInstruction] =
    useState('');

  const firmUserOptions = useMemo(
    () =>
      firmUsers?.map((user) => ({
        name: `${user.fname} ${user.lname}`,
        value: user.id.toString(),
      })) || [],
    [firmUsers],
  );

  // Function to reset the create drop link form
  const resetCreateDropLinkForm = useCallback(() => {
    setNewDropLinkName('');
    setNewDropLinkHideSignIn(false);
    setNewDropLinkUserIds(new Set());
    setNewDropLinkCustomInstruction('');
  }, []);

  // Dialog handlers
  const onCreateDialogClose = useCallback(() => {
    setShowCreateModal(false);
    resetCreateDropLinkForm();
  }, [resetCreateDropLinkForm]);

  const onEditDialogClose = useCallback(() => {
    setShowEditModal(false);
    setSelectedDropLink(null);
  }, []);

  const onUploadsDialogClose = useCallback(() => {
    setShowUploadsFor(null);
  }, []);

  const unseenDropLinkUploadsCount = useMemo(() => {
    if (!user) return 0;
    if (!dropLinks) return 0;

    return dropLinks.reduce((count, dropLink) => {
      return (
        count +
        (dropLink.unseen_user_ids_mapped?.filter((ids) => ids.includes(user.id))
          .length ?? 0)
      );
    }, 0);
  }, [dropLinks, user]);

  // Always load drop links on mount and when token changes

  const loadDropLinks = useCallback(async () => {
    if (!token || !isAdvisor || !preferences?.drop_link_enabled) {
      return;
    }

    try {
      const data = await getDropLinks({ token: token as string });
      if (data?.error) {
        addToast('error', 'Failed to load drop links');
      } else if (Array.isArray(data)) {
        setDropLinks(data);
      } else {
        addToast('error', 'Failed to load drop links');
      }
    } catch (error) {
      addToast('error', 'Failed to load drop links');
    }
  }, [token, addToast, isAdvisor, preferences?.drop_link_enabled]);

  const handleCreateDropLink = useCallback(async () => {
    invariant(token, 'Token is required to create drop link');
    invariant(newDropLinkName, 'Drop link name is required');
    invariant(
      newDropLinkUserIds.size > 0,
      'At least one user must be selected to notify',
    );

    // Check for duplicate names within the same firm
    const isDuplicate = dropLinks.some(
      (link) => link.name.toLowerCase() === newDropLinkName.toLowerCase(),
    );
    if (isDuplicate) {
      addToast('error', 'A drop link with this name already exists');
      return;
    }

    setIsLoading(true);
    try {
      const response = await createDropLinkAPI({
        token: token as string,
        name: newDropLinkName,
        hideSignInButton: newDropLinkHideSignIn,
        subscribedUserIds: Array.from(newDropLinkUserIds).map(Number),
        customInstruction: newDropLinkCustomInstruction,
      });
      if (response?.error) {
        addToast('error', 'Failed to create drop link');
      } else if ('id' in response) {
        // Transform the response to include subscribed_firm_users
        const transformedResponse = {
          ...response,
          subscribed_user_ids: Array.from(newDropLinkUserIds).map(Number),
        };
        setDropLinks((prev) => [
          ...(prev || []),
          transformedResponse as DropLink,
        ]);
        addToast('success', 'Drop link created successfully');
        setShowCreateModal(false);
        // Reset form
        resetCreateDropLinkForm();
      } else {
        addToast('error', 'Failed to create drop link');
      }
    } catch (error) {
      addToast('error', 'Failed to create drop link');
    } finally {
      setIsLoading(false);
    }
  }, [
    token,
    addToast,
    newDropLinkName,
    newDropLinkHideSignIn,
    newDropLinkUserIds,
    newDropLinkCustomInstruction,
    dropLinks,
    resetCreateDropLinkForm,
  ]);

  const updateDropLink = useCallback(
    async (dropLink: DropLink) => {
      invariant(dropLink, 'Drop link is required');
      invariant(dropLink.id, 'Drop link ID is required');
      if (!token) {
        addToast('error', 'Token is required to update drop link');
        return;
      }
      if (!dropLink.name) {
        addToast('error', 'Drop link name is required');
        return;
      }
      if (
        !dropLink.subscribed_user_ids ||
        dropLink.subscribed_user_ids.length === 0
      ) {
        addToast('error', 'At least one user must be selected to notify');
        return;
      }

      // Check for duplicate names within the same firm (excluding the current drop link)
      const isDuplicate = dropLinks.some(
        (link) =>
          link.id !== dropLink.id &&
          link.name.toLowerCase() === dropLink.name?.toLowerCase(),
      );
      if (isDuplicate) {
        addToast('error', 'A drop link with this name already exists');
        return;
      }

      setIsLoading(true);
      try {
        const response = await updateDropLinkAPI({
          token: token as string,
          dropLink,
        });
        if (response?.error) {
          addToast('error', 'Failed to update drop link');
        } else if ('id' in response) {
          const updatedDropLink = response as DropLink;
          setDropLinks(
            (prev) =>
              prev?.map((link) =>
                link.id === dropLink.id ? updatedDropLink : link,
              ) || [],
          );
          addToast('success', 'Drop link updated successfully');
          setSelectedDropLink(null);
          setShowEditModal(false);
        } else {
          addToast('error', 'Failed to update drop link');
        }
      } catch (error) {
        addToast('error', 'Failed to update drop link');
      } finally {
        setIsLoading(false);
      }
    },
    [token, addToast, dropLinks],
  );

  const deleteDropLink = useCallback(
    async (id: number) => {
      invariant(token, 'Token is required to delete drop link');
      invariant(id, 'Drop link ID is required');

      try {
        const response = await deleteDropLinkAPI({
          id,
          token: token as string,
        });
        if (response?.error) {
          addToast('error', 'Failed to delete drop link');
        } else {
          setDropLinks((prev) => prev.filter((link) => link.id !== id));
          addToast('success', 'Drop link deleted successfully');
        }
      } catch (error) {
        addToast('error', 'Failed to delete drop link');
      }
    },
    [token, addToast],
  );

  const closeAllDropLinkDialogs = useCallback(() => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowUploadsFor(null);
  }, []);

  return (
    <DropLinksContext
      value={{
        // Dialog states
        showCreateModal,
        showEditModal,
        showUploadsFor,

        // Dialog handlers
        onCreateDialogClose,
        onEditDialogClose,
        onUploadsDialogClose,

        // Drop link states
        dropLinks,
        isLoading,
        selectedDropLink,
        newDropLinkName,
        newDropLinkHideSignIn,
        newDropLinkUserIds,
        newDropLinkCustomInstruction,
        firmUserOptions,
        isActive,
        unseenDropLinkUploadsCount,

        // State setters
        setActive,
        setSelectedDropLink,
        setNewDropLinkName,
        setNewDropLinkHideSignIn,
        setNewDropLinkUserIds,
        setNewDropLinkCustomInstruction,
        setShowCreateModal,
        setShowEditModal,
        setShowUploadsFor,

        // Actions
        handleCreateDropLink,
        updateDropLink,
        deleteDropLink,
        loadDropLinks,
        resetCreateDropLinkForm,
        closeAllDropLinkDialogs,
      }}
    >
      {showUploadsFor && (
        <DropLinkUploadsDialog
          dropLink={showUploadsFor}
          isOpen={!!showUploadsFor}
          onClose={onUploadsDialogClose}
        />
      )}

      {showCreateModal && (
        <CreateDropLinkDialog
          isOpen={showCreateModal}
          onClose={onCreateDialogClose}
        />
      )}

      {showEditModal && (
        <EditDropLinkDialog
          isOpen={showEditModal}
          onClose={onEditDialogClose}
        />
      )}

      {children}
    </DropLinksContext>
  );
};

export { DropLinksProvider, DropLinksContext };
